import { useEffect, useReducer, useCallback } from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';

const ACTIONS = {
  SET_ENTITY_TYPE: 'SET_ENTITY_TYPE',
  SEARCH_CHANGE: 'SEARCH_CHANGE',
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  FETCH_CLEAR: 'FETCH_CLEAR',
  SORT_CHANGED: 'SORT_CHANGED',
};

const reduceToString = (obj) => {
  return Object.keys(obj)
    .sort()
    .map((key) => `${key}=${obj[key]}`)
    .join(',');
};
const getFiltersKey = (filters, sort) => {
  return reduceToString(filters) + reduceToString(sort);
};

const dataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_ENTITY_TYPE:
      return {
        ...state,
        resultsBySearch: {},
      };
    case ACTIONS.SEARCH_CHANGE:
      return {
        ...state,
        filters: action.payload,
      };
    case ACTIONS.FETCH_INIT: {
      const { filters, sort } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      return {
        ...state,
        resultsBySearch: {
          ...state.resultsBySearch,
          [filtersKey]: {
            ...state.resultsBySearch[filtersKey],
            loading: true,
            error: null,
            onTryAgain: null,
          },
        },
      };
    }
    case ACTIONS.FETCH_SUCCESS: {
      const { filters, sort } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      return {
        ...state,
        resultsBySearch: {
          ...state.resultsBySearch,
          [filtersKey]: {
            ...state.resultsBySearch[filtersKey],
            loading: false,
            error: null,
            onTryAgain: null,
            entities: (
              (state.resultsBySearch[filtersKey] &&
                state.resultsBySearch[filtersKey].entities) ||
              []
            ).concat(action.payload.entities),
            onLoadMore: action.payload.onLoadMore,
          },
        },
      };
    }
    case ACTIONS.FETCH_FAILURE: {
      const { filters, sort } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      return {
        ...state,
        resultsBySearch: {
          ...state.resultsBySearch,
          [filtersKey]: {
            ...state.resultsBySearch[filtersKey],
            loading: false,
            error: action.payload.error,
            onTryAgain: action.payload.onTryAgain,
          },
        },
      };
    }
    case ACTIONS.FETCH_CLEAR: {
      const { filters, sort } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      const newState = { ...state };
      delete newState.resultsBySearch[filtersKey];
      return newState;
    }
    case ACTIONS.SORT_CHANGED:
      return {
        ...state,
        sort: action.payload,
      };
    default:
      throw new Error();
  }
};

/**
 * This hook returns data for searched entities, using given plugin to fetch the relevant data.
 * Returned value:
 * entities - the list of entities retrieved for current search expression.
 * loading - whether it currently loads data from BE.
 * error - whether fetching data raised an error.
 * onLoadMore - callback to load more entities.
 * onTryAgain - callback to re-fetch data from BE.
 * filters - the current filters.
 * setFilters - callback to set the current filter.
 *
 * @param plugin
 * @param pageLimit
 * @param delay
 * @param addEntitiesToCache
 * @returns {{loading: *, onLoadMore: *, filters: *, onTryAgain: *, entities: *, setFilters: *, error: *}}
 */
export default ({
  plugin,
  pageLimit = 10,
  delay = 500,
  addEntitiesToCache,
}) => {
  const [state, dispatch] = useReducer(dataReducer, {
    filters: {},
    resultsBySearch: {},
    sort: {},
  });
  const currentProject = useCurrentProject();
  const projectId = currentProject?.id;
  const projectScope = currentProject?.scopes?.project;

  const { filters, resultsBySearch, sort } = state;
  const filtersKey = getFiltersKey(filters, sort);
  const {
    entities = [],
    loading = true,
    error,
    onTryAgain,
    onLoadMore,
  } = resultsBySearch[filtersKey] || {};
  const type = plugin?.metadata?.type;
  const domain = plugin?.metadata?.domain;
  useEffect(() => {
    dispatch({ type: ACTIONS.SET_ENTITY_TYPE });
  }, [type, domain]);

  useEffect(() => {
    let handler,
      unmounted = false;
    if (plugin && projectId && !resultsBySearch[filtersKey]) {
      handler = setTimeout(() => {
        const readData = (query) => {
          dispatch({ type: ACTIONS.FETCH_INIT, payload: { filters, sort } });
          return plugin
            .queryEntities(query)
            .then(({ data, nextPageQuery }) => {
              addEntitiesToCache &&
                addEntitiesToCache({ entities: data, domain, type });
              dispatch({
                type: ACTIONS.FETCH_SUCCESS,
                payload: {
                  filters,
                  sort,
                  entities: data,
                  onLoadMore: nextPageQuery
                    ? () => readData(nextPageQuery)
                    : null,
                },
              });
            })
            .catch((e) => {
              console.error(
                'ReferencePicker - useEntitySearch: Error searching entities',
                e,
              );
              if (unmounted) {
                dispatch({
                  type: ACTIONS.FETCH_CLEAR,
                  payload: {
                    filters,
                    sort,
                  },
                });
              } else {
                dispatch({
                  type: ACTIONS.FETCH_FAILURE,
                  payload: {
                    filters,
                    sort,
                    error: e,
                    onTryAgain: () => readData(query),
                  },
                });
              }
            });
        };
        readData({ filters, pageLimit, projectId, projectScope, sort });
      }, delay);
    }
    return () => {
      unmounted = true;
      handler && clearTimeout(handler);
    };
    // Disable as we don't want to refetch when `resultsBySearch` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugin, filters, projectId, projectScope, delay, sort]);

  const setFilters = useCallback((newFilters) => {
    dispatch({ type: ACTIONS.SEARCH_CHANGE, payload: newFilters });
  }, []);

  const setSort = useCallback((sortOptions) => {
    dispatch({ type: ACTIONS.SORT_CHANGED, payload: sortOptions });
  }, []);

  return {
    entities,
    loading,
    error,
    onTryAgain,
    onLoadMore,
    filters,
    setFilters,
    sort,
    setSort,
  };
};
